import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { authService, passwordService, validatePasswordStrength } from '../../../services/authService';

type Step = 'request' | 'verify' | 'reset';

const RESEND_COOLDOWN_SECONDS = 60;

/**
 * Change password for a signed-in user.
 *
 * Three steps, so a wrong code is caught before the user types any passwords:
 *   request → send the code
 *   verify  → prove the code, swap it for a short-lived pwdChangeToken cookie
 *   reset   → current password + new password
 *
 * Requires BOTH the OTP and the current password. The OTP alone would let
 * anyone holding a stolen session change the password; the current password
 * alone would not prove control of the mailbox.
 *
 * A successful change ends the session server-side, so this screen always
 * finishes by sending the user back to the login page.
 */
function ChangePasswordPage() {
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>('request');
  const [maskedEmail, setMaskedEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const handleSendOtp = async () => {
    setError('');
    setNotice('');
    setLoading(true);

    try {
      const response = await passwordService.requestChangeOtp();
      setMaskedEmail(response.email || '');
      // On the first send the step hint already names the address. Only say
      // something extra when this is a resend, where confirmation is the point.
      setNotice(step === 'verify' ? 'A new code has been sent.' : '');
      setOtp('');
      setStep('verify');
      setCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (err: any) {
      setError(err.message || 'Could not send the verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setNotice('');
    setLoading(true);

    try {
      await passwordService.verifyChangeOtp(otp.trim());
      // Confirmation only — the step hint carries the instruction.
      setNotice('Code verified.');
      setStep('reset');
    } catch (err: any) {
      setError(err.message || 'Could not verify the code');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setNotice('');

    const policyError = validatePasswordStrength(newPassword);
    if (policyError) {
      setError(policyError);
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('The two new passwords do not match');
      return;
    }
    if (newPassword === currentPassword) {
      setError('The new password must be different from your current password');
      return;
    }

    setLoading(true);
    try {
      await passwordService.confirmChangePassword({ currentPassword, newPassword });

      // The backend has already cleared the session cookies — drop the local
      // copy of the user details so the app does not think it is still signed in.
      authService.logout();
      navigate('/login', { replace: true, state: { passwordReset: true } });
    } catch (err: any) {
      setError(err.message || 'Could not update the password');
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    'w-full border-b border-gray-300 focus:border-purple-600 outline-none py-2 px-1 text-sm md:text-base bg-transparent disabled:bg-gray-100';

  const eyeButton = (
    <button
      type="button"
      onClick={() => setShowPassword((p) => !p)}
      className="absolute right-1 top-1/2 -translate-y-1/2 text-gray-600 hover:text-gray-800"
      aria-label={showPassword ? 'Hide password' : 'Show password'}
    >
      {showPassword ? (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3l18 18M10.58 10.58A2 2 0 0013.42 13.42M9.88 5.09A9.77 9.77 0 0112 5c5 0 9 5 9 7a11.4 11.4 0 01-2.08 3.11M6.1 6.1C3.91 7.6 2.4 9.7 2 12c0 2 4 7 10 7a9.74 9.74 0 004.32-.99" />
        </svg>
      ) : (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
        </svg>
      )}
    </button>
  );

  return (
    // Background and full height come from PageShell (see AppRouter).
    <div className="w-full flex items-start justify-center px-4 py-12">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-6 md:p-8">
        <div className="flex items-center gap-3 mb-1">
          <svg width="28" height="28" fill="none" stroke="#6d28d9" strokeWidth="1.5" viewBox="0 0 24 24">
            <rect x="4" y="10" width="16" height="10" rx="2" />
            <path d="M8 10V7a4 4 0 018 0v3" />
          </svg>
          <h1 className="text-lg font-semibold text-purple-700">Change Password</h1>
        </div>

        {/* Step indicator */}
        <div className="flex items-center gap-2 my-4">
          {(['request', 'verify', 'reset'] as Step[]).map((s, i) => (
            <div
              key={s}
              className={`h-1.5 flex-1 rounded-full transition-colors ${
                (['request', 'verify', 'reset'] as Step[]).indexOf(step) >= i ? 'bg-purple-600' : 'bg-gray-200'
              }`}
            />
          ))}
        </div>

        <p className="text-xs text-gray-500 mb-4">
          {step === 'request' &&
            'We will email a verification code to the address registered on your account.'}
          {step === 'verify' &&
            `Enter the 6-digit code${maskedEmail ? ` sent to ${maskedEmail}` : ''}. Nothing changes until the code is verified.`}
          {step === 'reset' &&
            'Enter your current password and choose a new one. You will be logged out of all devices.'}
        </p>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-3 py-2 rounded text-sm mb-3">{error}</div>
        )}
        {notice && !error && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-3 py-2 rounded text-sm mb-3">{notice}</div>
        )}

        {/* ---------- Step 1: send the code ---------- */}
        {step === 'request' && (
          <>
            <button
              type="button"
              onClick={handleSendOtp}
              disabled={loading}
              className="w-full rounded-lg bg-gradient-to-r from-purple-600 to-purple-700 text-white font-semibold shadow-md hover:from-purple-700 hover:to-purple-800 transition-colors h-[46px] disabled:bg-purple-400 disabled:cursor-not-allowed"
            >
              {loading ? 'Sending...' : 'Send Verification Code'}
            </button>
            <div className="text-center mt-4">
              <Link to="/dashboard" className="text-xs text-gray-500 hover:text-purple-700">
                Back to Dashboard
              </Link>
            </div>
          </>
        )}

        {/* ---------- Step 2: verify the code ---------- */}
        {step === 'verify' && (
          <form onSubmit={handleVerifyOtp} autoComplete="off">
            <label htmlFor="otp" className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
              6-digit code
            </label>
            <input
              type="text"
              id="otp"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              inputMode="numeric"
              required
              autoFocus
              autoComplete="one-time-code"
              disabled={loading}
              className={`${inputClass} tracking-[0.5em] text-lg font-semibold`}
              onPaste={(e) => e.preventDefault()}
              onCopy={(e) => e.preventDefault()}
              onCut={(e) => e.preventDefault()}
            />

            <button
              type="button"
              onClick={handleSendOtp}
              disabled={loading || cooldown > 0}
              className="text-xs text-purple-700 hover:text-purple-900 disabled:text-gray-400 disabled:cursor-not-allowed mt-2"
            >
              {cooldown > 0 ? `Resend code in ${cooldown}s` : 'Resend code'}
            </button>

            <button
              type="submit"
              disabled={loading || otp.length !== 6}
              className="w-full rounded-lg bg-gradient-to-r from-purple-600 to-purple-700 text-white font-semibold shadow-md hover:from-purple-700 hover:to-purple-800 transition-colors mt-6 h-[46px] disabled:bg-purple-400 disabled:cursor-not-allowed"
            >
              {loading ? 'Verifying...' : 'Verify Code'}
            </button>

            <div className="text-center mt-4">
              <Link to="/dashboard" className="text-xs text-gray-500 hover:text-purple-700">
                Cancel
              </Link>
            </div>
          </form>
        )}

        {/* ---------- Step 3: passwords ---------- */}
        {step === 'reset' && (
          <form onSubmit={handleSubmit} autoComplete="off">
            <label htmlFor="currentPassword" className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
              Current Password
            </label>
            <input
              type="password"
              id="currentPassword"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              placeholder="Enter current password"
              required
              autoFocus
              autoComplete="current-password"
              disabled={loading}
              className={inputClass}
              onPaste={(e) => e.preventDefault()}
              onCopy={(e) => e.preventDefault()}
              onCut={(e) => e.preventDefault()}
            />

            <label htmlFor="newPassword" className="block text-xs md:text-sm font-medium text-gray-700 mb-1 mt-4">
              New Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                id="newPassword"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
                required
                autoComplete="new-password"
                disabled={loading}
                className={`${inputClass} pr-10`}
                onPaste={(e) => e.preventDefault()}
                onCopy={(e) => e.preventDefault()}
                onCut={(e) => e.preventDefault()}
              />
              {eyeButton}
            </div>

            <label htmlFor="confirmPassword" className="block text-xs md:text-sm font-medium text-gray-700 mb-1 mt-4">
              Confirm New Password
            </label>
            <input
              type={showPassword ? 'text' : 'password'}
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Re-enter new password"
              required
              autoComplete="new-password"
              disabled={loading}
              className={inputClass}
              onPaste={(e) => e.preventDefault()}
              onCopy={(e) => e.preventDefault()}
              onCut={(e) => e.preventDefault()}
            />

            {/* Same single line as ForgotPasswordPage — keeps the two password
                screens visually identical. */}
            <p className="text-[11px] text-gray-500 mt-3 leading-snug">
              At least 8 characters, with a capital, a small letter, a number and a special character.
            </p>

            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-lg bg-gradient-to-r from-purple-600 to-purple-700 text-white font-semibold shadow-md hover:from-purple-700 hover:to-purple-800 transition-colors mt-4 h-[46px] disabled:bg-purple-400 disabled:cursor-not-allowed"
            >
              {loading ? 'Updating...' : 'Update Password'}
            </button>

            <div className="text-center mt-4">
              <Link to="/dashboard" className="text-xs text-gray-500 hover:text-purple-700">
                Cancel
              </Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default ChangePasswordPage;
